import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Fab,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Inventory2 as Inventory2Icon,
  AccountBalance as AccountBalanceIcon,
  CardMembership as CardMembershipIcon,
} from '@mui/icons-material';
import { isAuthenticated, getCurrentUser } from '../services/authService';
import { customerService } from '../services/customerService';
import { supplierService } from '../services/supplierService';
import { getDashboardConfig } from '../services/dashboardSettingsService';
import {
  mergeDashboardConfig,
  isSectionVisible,
  filterOverviewCards,
  filterLowStockItems,
  isDashboardAlertEnabled,
  resolveQuickActions,
  needsOverviewCard,
  canEditDashboardSettings,
  getGridColumnSpan,
  countVisibleSections,
  normalizeSalesOverviewTemplate,
} from '../utils/dashboardConfigUtils';
import Layout from '../components/layout/Layout';
import PageTitle from '../components/PageTitle';
import dashboardService from '../services/dashboardService';
import creditService from '../services/creditService';
import locationDashboardService from '../services/locationDashboardService';
import topSellingProductsService from '../services/topSellingProductsService';
import expenseService from '../services/expenseService';
import activityLogService from '../services/activityLogService';
import { getStockReport, getInvoiceProfitLossReport } from '../services/reportService';
import { formatCurrency } from '../utils/formatCurrency';
import TrialReminderDialog from '../components/saas/TrialReminderDialog';
import SubscriptionReminderDialog from '../components/saas/SubscriptionReminderDialog';
import { getTrialStatus, getSubscriptionStatus } from '../services/companySubscriptionService';
import TodayOverview, { OVERVIEW_CARD_THEMES } from '../components/dashboard/TodayOverview';
import QuickActions from '../components/dashboard/QuickActions';
import { getOverviewDateRange, getTimeRangeLabel, getBestSellersDateRange } from '../utils/dashboardOverviewUtils';
import AlertsPanel from '../components/dashboard/AlertsPanel';
import LowStockTable from '../components/dashboard/LowStockTable';
import BestSellersTable from '../components/dashboard/BestSellersTable';
import PendingPaymentsList from '../components/dashboard/PendingPaymentsList';
import RecentActivityFeed from '../components/dashboard/RecentActivityFeed';
import InventoryHealthGrid from '../components/dashboard/InventoryHealthGrid';
import SalesOverviewRenderer from '../components/dashboard/salesOverview/SalesOverviewRenderer';
import { fetchSalesOverviewByPeriod } from '../services/salesOverviewPeriodService';
import { DASHBOARD_COLORS } from '../components/dashboard/dashboardStyles';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import useDashboardShortcuts from '../hooks/useDashboardShortcuts';
import AskGeminiFab from '../components/dashboard/ai/AskGeminiFab';
import AiChatDrawer from '../components/dashboard/ai/AiChatDrawer';
import { getCurrentSubscription } from '../services/subscriptionService';
import aiAssistantService, { getAiAssistantStatus } from '../services/aiAssistantService';
import buildDashboardAiSnapshot from '../utils/dashboardAiSnapshot';
import { INSIGHT_CONTEXT, OVERVIEW_CARD_AI_CONTEXT } from '../utils/dashboardInsightAi';

/** Location filter for dashboard reports — omit for admin (all locations, same as report page). */
const getDashboardLocationId = () => {
  try {
    const stored = localStorage.getItem('user');
    if (!stored) return undefined;
    const parsed = JSON.parse(stored);
    const role = (parsed.role || localStorage.getItem('userRole') || '').toUpperCase();
    if (role === 'ADMIN') return undefined;
    if (parsed.location?.id != null) return String(parsed.location.id);
  } catch {
    /* ignore */
  }
  return undefined;
};

const mapTopSellingProducts = (report) =>
  (report?.products || []).slice(0, 5).map((p) => ({
    productId: p.productId,
    productName: p.productName,
    quantitySold: p.quantitySold,
    totalRevenue: p.totalAmount ?? p.totalRevenue ?? 0,
  }));

/** Align with Reports → Stock (reorder_level + quantity). */
const mapStockReportToDashboard = (stockReport) => {
  const summary = stockReport?.summary || {};
  const stockItems = stockReport?.stockItems || [];
  const attentionItems = stockItems
    .filter((item) => item.stockStatus === 'low' || item.stockStatus === 'out')
    .sort((a, b) => Number(a.quantity) - Number(b.quantity))
    .map((item) => ({
      productId: item.productId,
      productName: item.productName,
      currentStock: item.quantity,
      minStockLevel: item.minStockLevel,
      stockStatus: item.stockStatus,
    }));

  return {
    inventoryStats: {
      totalProducts: Number(summary.totalProducts) || 0,
      inStock: Number(summary.inStock) || 0,
      lowStock: Number(summary.lowStock) || 0,
      outOfStock: Number(summary.outOfStock) || 0,
    },
    lowStockItems: attentionItems,
  };
};

/** Top 5 by due amount, with last credit sale date for "Last Purchase" column. */
const buildTopPendingPayments = async (customers) => {
  const top = [...(customers || [])]
    .sort((a, b) => Number(b.balanceDue || 0) - Number(a.balanceDue || 0))
    .slice(0, 5);

  return Promise.all(
    top.map(async (customer) => {
      try {
        const txs = await creditService.getCustomerTransactions(customer.id);
        const creditSales = (txs || [])
          .filter((t) => String(t.transactionType || '').toUpperCase() === 'CREDIT_SALE')
          .sort((a, b) => new Date(b.transactionDate || 0) - new Date(a.transactionDate || 0));
        return {
          ...customer,
          lastPurchaseDate: creditSales[0]?.transactionDate || null,
        };
      } catch {
        return customer;
      }
    })
  );
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const user = getCurrentUser();
  const [loading, setLoading] = useState(true);
  const [trialReminderOpen, setTrialReminderOpen] = useState(false);
  const [subscriptionReminderOpen, setSubscriptionReminderOpen] = useState(false);
  const [timeRange, setTimeRange] = useState('today');
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [overviewBills, setOverviewBills] = useState(0);
  const [overviewSalesAmount, setOverviewSalesAmount] = useState(0);
  const [overviewExpenses, setOverviewExpenses] = useState(0);
  const [overviewProfit, setOverviewProfit] = useState(0);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [totalSuppliers, setTotalSuppliers] = useState(0);
  const [dashboardConfig, setDashboardConfig] = useState(() => mergeDashboardConfig(null));
  const [totalDueAmount, setTotalDueAmount] = useState(0);
  const [totalCreditCustomers, setTotalCreditCustomers] = useState(0);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [bestSellersPeriod, setBestSellersPeriod] = useState('thisMonth');
  const [bestSellersLoading, setBestSellersLoading] = useState(false);
  const [creditCustomers, setCreditCustomers] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [chartLabels, setChartLabels] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [inventoryStats, setInventoryStats] = useState({
    totalProducts: 0,
    inStock: 0,
    lowStock: 0,
    outOfStock: 0,
  });
  const [salesMetrics, setSalesMetrics] = useState({
    primary: 0,
    secondary: 0,
    dailyAverage: 0,
  });
  const [salesOverviewPeriod, setSalesOverviewPeriod] = useState('monthly');
  const [salesChartLoading, setSalesChartLoading] = useState(false);
  const [trialStatus, setTrialStatus] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [aiAssistantEnabled, setAiAssistantEnabled] = useState(false);
  const [aiChatOpen, setAiChatOpen] = useState(false);
  const [aiInsightContext, setAiInsightContext] = useState(null);
  const [aiSnapshotVersion, setAiSnapshotVersion] = useState(0);

  const openAiForInsight = useCallback((contextId) => {
    setAiInsightContext(contextId);
    setAiChatOpen(true);
  }, []);

  const openAiChatGeneral = useCallback(() => {
    setAiInsightContext(null);
    setAiChatOpen(true);
  }, []);

  const quickActions = useMemo(
    () => resolveQuickActions(dashboardConfig),
    [dashboardConfig]
  );

  useDashboardShortcuts(navigate, isAuthenticated(), quickActions);

  useEffect(() => {
    if (!isAuthenticated()) return;
    getDashboardConfig()
      .then(setDashboardConfig)
      .catch(() => setDashboardConfig(mergeDashboardConfig(null)));
  }, []);

  const refreshAiAssistantEnabled = useCallback(() => {
    getAiAssistantStatus()
      .then((status) => {
        setAiAssistantEnabled(Boolean(status?.enabled));
      })
      .catch(() => {
        getCurrentSubscription()
          .then((sub) => {
            const features = sub?.plan?.features;
            setAiAssistantEnabled(aiAssistantService.isPlanFeatureEnabled(features, 'ai_assistant'));
          })
          .catch(() => setAiAssistantEnabled(false));
      });
  }, []);

  useEffect(() => {
    if (!isAuthenticated()) return;
    refreshAiAssistantEnabled();
  }, [refreshAiAssistantEnabled]);

  useEffect(() => {
    const onPlanFeaturesUpdated = () => refreshAiAssistantEnabled();
    window.addEventListener('planFeaturesUpdated', onPlanFeaturesUpdated);
    return () => window.removeEventListener('planFeaturesUpdated', onPlanFeaturesUpdated);
  }, [refreshAiAssistantEnabled]);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/');
      return;
    }

    const checkTrialStatus = async () => {
      try {
        const data = await getTrialStatus();
        if (data.trialActive && !sessionStorage.getItem('trialReminderDismissed')) {
          setTrialReminderOpen(true);
        }
      } catch {
        /* ignore */
      }
    };

    const checkSubscriptionStatus = async () => {
      try {
        const data = await getSubscriptionStatus();
        if (
          data.active &&
          data.daysRemaining != null &&
          data.daysRemaining <= 3 &&
          !sessionStorage.getItem('subscriptionReminderDismissed')
        ) {
          setSubscriptionReminderOpen(true);
        }
      } catch {
        /* ignore */
      }
    };

    checkTrialStatus();
    checkSubscriptionStatus();
  }, [navigate]);

  const loadOverviewStats = useCallback(async (range) => {
    setOverviewLoading(true);
    const { startDate, endDate, isToday } = getOverviewDateRange(range);

    try {
      let bills = 0;
      let salesAmount = 0;
      const needBills = needsOverviewCard(dashboardConfig, 'bills');
      const needSales = needsOverviewCard(dashboardConfig, 'sales');

      if (needBills || needSales) {
        if (isToday) {
          const todaysSalesRes = await locationDashboardService.getTodaysSales().catch(() => ({}));
          bills = Number(todaysSalesRes.totalOrders ?? 0);
          salesAmount = Number(todaysSalesRes.totalSales ?? todaysSalesRes.totalRevenue ?? 0);
        } else {
          const report = await locationDashboardService.getSalesReport(startDate, endDate).catch(() => ({}));
          bills = Number(report.totalOrders ?? 0);
          salesAmount = Number(report.totalSales ?? report.totalRevenue ?? 0);
        }
      }

      let expenseTotal = 0;
      if (needsOverviewCard(dashboardConfig, 'expenses')) {
        const expensesRes = await expenseService
          .listExpenses({ page: 0, size: 1000, fromDate: startDate, toDate: endDate })
          .catch(() => ({ content: [] }));
        expenseTotal = (expensesRes.content || []).reduce(
          (sum, row) => sum + (Number(row.amount) || 0),
          0
        );
      }

      let profitTotal = 0;
      if (needsOverviewCard(dashboardConfig, 'profit')) {
        const locationId = getDashboardLocationId();
        const profitParams = { startDate, endDate };
        if (locationId) profitParams.locationId = locationId;
        const profitRes = await getInvoiceProfitLossReport(profitParams).catch(() => ({
          totalProfit: 0,
          totalLoss: 0,
        }));
        profitTotal =
          (Number(profitRes.totalProfit) || 0) - (Number(profitRes.totalLoss) || 0);
      }

      setOverviewBills(bills);
      setOverviewSalesAmount(salesAmount);
      setOverviewExpenses(expenseTotal);
      setOverviewProfit(profitTotal);
    } catch (err) {
      console.error('Overview stats load error:', err);
      setOverviewBills(0);
      setOverviewSalesAmount(0);
      setOverviewExpenses(0);
      setOverviewProfit(0);
    } finally {
      setOverviewLoading(false);
    }
  }, [dashboardConfig]);

  const loadBestSellers = useCallback(async () => {
    if (!isSectionVisible(dashboardConfig, 'best_sellers')) {
      return;
    }

    setBestSellersLoading(true);
    try {
      const { startDate, endDate } = getBestSellersDateRange(bestSellersPeriod);
      const dashboardLocationId = getDashboardLocationId();
      const report = await topSellingProductsService
        .getTopSellingProducts(dashboardLocationId, startDate, endDate)
        .catch(() => ({ products: [] }));
      setTopProducts(mapTopSellingProducts(report));
    } catch (err) {
      console.error('Best sellers load error:', err);
      setTopProducts([]);
    } finally {
      setBestSellersLoading(false);
    }
  }, [dashboardConfig, bestSellersPeriod]);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    const dashboardLocationId = getDashboardLocationId();

    try {
      const stockReportParams = {};
      if (dashboardLocationId) {
        stockReportParams.locationId = dashboardLocationId;
      }

      const alertsPanelVisible = isSectionVisible(dashboardConfig, 'alerts');
      const needStock =
        isSectionVisible(dashboardConfig, 'inventory_health') ||
        isSectionVisible(dashboardConfig, 'low_stock') ||
        needsOverviewCard(dashboardConfig, 'lowStock') ||
        (alertsPanelVisible &&
          (isDashboardAlertEnabled(dashboardConfig, 'low-stock') ||
            isDashboardAlertEnabled(dashboardConfig, 'out-of-stock')));
      const needSalesChart = isSectionVisible(dashboardConfig, 'sales_overview');
      const needCredit =
        isSectionVisible(dashboardConfig, 'pending_payments') ||
        needsOverviewCard(dashboardConfig, 'pending') ||
        needsOverviewCard(dashboardConfig, 'creditCustomers') ||
        (alertsPanelVisible && isDashboardAlertEnabled(dashboardConfig, 'credit-due'));
      const needActivity = isSectionVisible(dashboardConfig, 'recent_activity');
      const needCustomers = needsOverviewCard(dashboardConfig, 'customers');
      const needSuppliers = needsOverviewCard(dashboardConfig, 'suppliers');

      const [
        stockReportRes,
        creditRes,
        activityRes,
        customersRes,
        suppliersRes,
        trialRes,
        subscriptionRes,
      ] = await Promise.all([
        needStock
          ? getStockReport(stockReportParams).catch(() => ({ summary: {}, stockItems: [] }))
          : Promise.resolve({ summary: {}, stockItems: [] }),
        needCredit
          ? dashboardService.getCreditCustomersData().catch(() => ({
              customers: [],
              totalDueAmount: 0,
              totalCreditCustomers: 0,
            }))
          : Promise.resolve({ customers: [], totalDueAmount: 0, totalCreditCustomers: 0 }),
        needActivity
          ? activityLogService.getActivityLogs({ page: 0, size: 8 }).catch(() => ({ content: [] }))
          : Promise.resolve({ content: [] }),
        needCustomers
          ? customerService
              .getAllCustomers({ page: 0, size: 1000, sort: 'name', direction: 'ASC' })
              .catch(() => ({ content: [] }))
          : Promise.resolve({ content: [] }),
        needSuppliers
          ? supplierService.getAllSuppliers({ page: 0, size: 1 }).catch(() => ({ content: [], totalElements: 0 }))
          : Promise.resolve({ content: [], totalElements: 0 }),
        getTrialStatus().catch(() => ({})),
        getSubscriptionStatus().catch(() => ({})),
      ]);

      setTotalCustomers((customersRes.content || []).length);
      setTotalSuppliers(suppliersRes.totalElements ?? (suppliersRes.content || []).length ?? 0);

      const { inventoryStats: stockStats, lowStockItems: stockAttentionItems } =
        mapStockReportToDashboard(stockReportRes);

      setTrialStatus(trialRes);
      setSubscriptionStatus(subscriptionRes);

      setLowStockItems(filterLowStockItems(stockAttentionItems, dashboardConfig));
      setInventoryStats(stockStats);

      setTotalDueAmount(creditRes.totalDueAmount || 0);
      setTotalCreditCustomers(creditRes.totalCreditCustomers || 0);
      const pendingTop = await buildTopPendingPayments(creditRes.customers || []);
      setCreditCustomers(pendingTop);

      setActivityLogs((activityRes.content || []).slice(0, 8));
    } catch (err) {
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  }, [dashboardConfig]);

  const loadSalesOverview = useCallback(async () => {
    if (!isSectionVisible(dashboardConfig, 'sales_overview')) {
      return;
    }
    setSalesChartLoading(true);
    try {
      const overview = await fetchSalesOverviewByPeriod(salesOverviewPeriod);
      setChartLabels(overview.labels);
      setChartData(overview.data);
      setSalesMetrics(overview.metrics);
    } catch (err) {
      console.error('Sales overview load error:', err);
      setChartLabels([]);
      setChartData([]);
      setSalesMetrics({ primary: 0, secondary: 0, dailyAverage: 0 });
    } finally {
      setSalesChartLoading(false);
    }
  }, [dashboardConfig, salesOverviewPeriod]);

  useEffect(() => {
    if (isAuthenticated()) {
      loadDashboard();
    }
  }, [loadDashboard]);

  useEffect(() => {
    if (isAuthenticated()) {
      loadSalesOverview();
    }
  }, [loadSalesOverview]);

  useEffect(() => {
    if (isAuthenticated()) {
      loadBestSellers();
    }
  }, [loadBestSellers]);

  useEffect(() => {
    if (isAuthenticated()) {
      loadOverviewStats(timeRange);
    }
  }, [timeRange, loadOverviewStats]);

  const alerts = useMemo(() => {
    const list = [];

    if (
      isDashboardAlertEnabled(dashboardConfig, 'out-of-stock') &&
      inventoryStats.outOfStock > 0
    ) {
      list.push({
        id: 'out-of-stock',
        icon: Inventory2Icon,
        message: `${inventoryStats.outOfStock} product${inventoryStats.outOfStock !== 1 ? 's are' : ' is'} out of stock`,
        severity: 'urgent',
        actionLabel: 'View',
        onAction: () => navigate('/reports/stock'),
      });
    }

    if (
      isDashboardAlertEnabled(dashboardConfig, 'low-stock') &&
      inventoryStats.lowStock > 0
    ) {
      list.push({
        id: 'low-stock',
        icon: Inventory2Icon,
        message: `${inventoryStats.lowStock} product${inventoryStats.lowStock !== 1 ? 's are' : ' is'} low in stock`,
        severity: 'urgent',
        actionLabel: 'View',
        onAction: () => navigate('/reports/stock'),
      });
    }

    if (isDashboardAlertEnabled(dashboardConfig, 'credit-due') && totalDueAmount > 0) {
      list.push({
        id: 'credit-due',
        icon: AccountBalanceIcon,
        message: `${formatCurrency(totalDueAmount)} pending from ${totalCreditCustomers} customer${totalCreditCustomers !== 1 ? 's' : ''}`,
        severity: 'urgent',
        actionLabel: 'View',
        onAction: () => navigate('/reports/credit-customers'),
      });
    }

    if (isDashboardAlertEnabled(dashboardConfig, 'plan-days')) {
      if (trialStatus?.trialActive && trialStatus.daysRemaining != null) {
        const days = trialStatus.daysRemaining;
        list.push({
          id: 'plan-days',
          icon: CardMembershipIcon,
          message: `${days} day${days !== 1 ? 's' : ''} remaining on your free trial`,
          severity: days <= 7 ? 'warning' : 'info',
          actionLabel: 'View',
          onAction: () => navigate('/subscription'),
        });
      } else if (subscriptionStatus?.active && subscriptionStatus.daysRemaining != null) {
        const days = subscriptionStatus.daysRemaining;
        const planLabel = subscriptionStatus.planName ? ` (${subscriptionStatus.planName})` : '';
        list.push({
          id: 'plan-days',
          icon: CardMembershipIcon,
          message: `${days} day${days !== 1 ? 's' : ''} remaining on your current plan${planLabel}`,
          severity: days <= 7 ? 'warning' : 'info',
          actionLabel: 'View',
          onAction: () => navigate('/subscription'),
        });
      }
    }

    return list;
  }, [
    inventoryStats.lowStock,
    inventoryStats.outOfStock,
    totalDueAmount,
    totalCreditCustomers,
    trialStatus,
    subscriptionStatus,
    dashboardConfig,
    navigate,
  ]);

  const handleCloseTrialReminder = () => {
    setTrialReminderOpen(false);
    sessionStorage.setItem('trialReminderDismissed', 'true');
  };

  const handleCloseSubscriptionReminder = () => {
    setSubscriptionReminderOpen(false);
    sessionStorage.setItem('subscriptionReminderDismissed', 'true');
  };

  const getDashboardLocationScope = () => {
    const locationId = getDashboardLocationId();
    return locationId != null ? `location_${locationId}` : 'all_locations';
  };

  const buildAiSnapshot = useCallback(
    () =>
      buildDashboardAiSnapshot({
        timeRange,
        salesOverviewPeriod,
        overviewBills,
        overviewSalesAmount,
        overviewExpenses,
        overviewProfit,
        totalCustomers,
        totalSuppliers,
        totalDueAmount,
        totalCreditCustomers,
        inventoryStats,
        lowStockItems,
        topProducts,
        creditCustomers,
        activityLogs,
        chartLabels,
        chartData,
        salesMetrics,
        trialStatus,
        subscriptionStatus,
        locationScope: getDashboardLocationScope(),
      }),
    [
      timeRange,
      salesOverviewPeriod,
      overviewBills,
      overviewSalesAmount,
      overviewExpenses,
      overviewProfit,
      totalCustomers,
      totalSuppliers,
      totalDueAmount,
      totalCreditCustomers,
      inventoryStats,
      lowStockItems,
      topProducts,
      creditCustomers,
      activityLogs,
      chartLabels,
      chartData,
      salesMetrics,
      trialStatus,
      subscriptionStatus,
    ]
  );

  useEffect(() => {
    setAiSnapshotVersion((v) => v + 1);
  }, [
    timeRange,
    salesOverviewPeriod,
    overviewBills,
    overviewSalesAmount,
    overviewExpenses,
    overviewProfit,
    totalCustomers,
    totalDueAmount,
    inventoryStats,
    lowStockItems,
    topProducts,
    creditCustomers,
    chartLabels,
    chartData,
    salesMetrics,
  ]);

  const allOverviewCards = useMemo(
    () => [
      {
        id: 'bills',
        label: 'Total Bills',
        value: overviewBills,
        accentColor: OVERVIEW_CARD_THEMES.bills.color,
        icon: OVERVIEW_CARD_THEMES.bills.icon,
        onClick: () => navigate('/sales/view-orders'),
      },
      {
        id: 'sales',
        label: 'Total Sales Amount',
        value: formatCurrency(overviewSalesAmount),
        accentColor: OVERVIEW_CARD_THEMES.sales.color,
        icon: OVERVIEW_CARD_THEMES.sales.icon,
        onClick: () => navigate('/reports/location-sales'),
      },
      {
        id: 'customers',
        label: 'Total Customers',
        value: totalCustomers,
        accentColor: OVERVIEW_CARD_THEMES.customers.color,
        icon: OVERVIEW_CARD_THEMES.customers.icon,
        onClick: () => navigate('/sales/customers'),
      },
      {
        id: 'pending',
        label: 'Total Pending Amount',
        value: formatCurrency(totalDueAmount),
        accentColor: OVERVIEW_CARD_THEMES.pending.color,
        icon: OVERVIEW_CARD_THEMES.pending.icon,
        onClick: () => navigate('/reports/credit-customers'),
      },
      {
        id: 'lowStock',
        label: 'Low Stock Items',
        value: inventoryStats.lowStock,
        accentColor: OVERVIEW_CARD_THEMES.lowStock.color,
        icon: OVERVIEW_CARD_THEMES.lowStock.icon,
        onClick: () => navigate('/reports/stock'),
      },
      {
        id: 'expenses',
        label: 'Total Expenses',
        value: formatCurrency(overviewExpenses),
        accentColor: OVERVIEW_CARD_THEMES.expenses.color,
        icon: OVERVIEW_CARD_THEMES.expenses.icon,
        onClick: () => navigate('/expenses/entry'),
      },
      {
        id: 'suppliers',
        label: 'Total Suppliers',
        value: totalSuppliers,
        accentColor: OVERVIEW_CARD_THEMES.suppliers.color,
        icon: OVERVIEW_CARD_THEMES.suppliers.icon,
        onClick: () => navigate('/people/suppliers'),
      },
      {
        id: 'profit',
        label: 'Total Profit',
        value: formatCurrency(overviewProfit),
        accentColor: OVERVIEW_CARD_THEMES.profit.color,
        icon: OVERVIEW_CARD_THEMES.profit.icon,
        onClick: () => navigate('/reports/invoice-profit-loss'),
      },
      {
        id: 'creditCustomers',
        label: 'Total Credit Customers',
        value: totalCreditCustomers,
        accentColor: OVERVIEW_CARD_THEMES.creditCustomers.color,
        icon: OVERVIEW_CARD_THEMES.creditCustomers.icon,
        onClick: () => navigate('/reports/credit-customers'),
      },
    ],
    [
      overviewBills,
      overviewSalesAmount,
      totalCustomers,
      totalDueAmount,
      inventoryStats.lowStock,
      overviewExpenses,
      totalSuppliers,
      overviewProfit,
      totalCreditCustomers,
      navigate,
    ]
  );

  const overviewCards = useMemo(() => {
    const filtered = filterOverviewCards(allOverviewCards, dashboardConfig);
    if (!aiAssistantEnabled) return filtered;
    return filtered.map((card) => {
      const ctx = OVERVIEW_CARD_AI_CONTEXT[card.id];
      if (!ctx) return card;
      return {
        ...card,
        showAiAssistant: true,
        onAiAssistantClick: () => openAiForInsight(ctx),
      };
    });
  }, [allOverviewCards, dashboardConfig, aiAssistantEnabled, openAiForInsight]);

  const inventoryRowColSpan = useMemo(() => {
    const count = countVisibleSections(dashboardConfig, [
      'inventory_health',
      'low_stock',
      'best_sellers',
    ]);
    return getGridColumnSpan(count);
  }, [dashboardConfig]);

  const paymentsRowColSpan = useMemo(() => {
    const count = countVisibleSections(dashboardConfig, ['pending_payments', 'recent_activity']);
    return getGridColumnSpan(count);
  }, [dashboardConfig]);

  if (!isAuthenticated()) {
    return null;
  }

  return (
    <Layout>
      <PageTitle title="Dashboard" />
      <TrialReminderDialog open={trialReminderOpen} onClose={handleCloseTrialReminder} />
      <SubscriptionReminderDialog open={subscriptionReminderOpen} onClose={handleCloseSubscriptionReminder} />

      <Box sx={{ bgcolor: DASHBOARD_COLORS.bg, minHeight: '100%', pb: 10 }}>
        <Container maxWidth="xl" sx={{ pt: 2 }}>
          <DashboardHeader
            userName={user?.name}
            timeRange={timeRange}
            onTimeRangeChange={setTimeRange}
            showSettings={canEditDashboardSettings()}
            onOpenSettings={() => navigate('/dashboard/settings')}
          />

          {isSectionVisible(dashboardConfig, 'overview') && overviewCards.length > 0 && (
            <TodayOverview
              title={`${getTimeRangeLabel(timeRange)} Overview`}
              cards={overviewCards}
              loading={overviewLoading}
            />
          )}

          {isSectionVisible(dashboardConfig, 'quick_actions') && quickActions.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <QuickActions onNavigate={navigate} actions={quickActions} />
            </Box>
          )}

          {(isSectionVisible(dashboardConfig, 'alerts') ||
            isSectionVisible(dashboardConfig, 'sales_overview')) && (
            <Grid container spacing={2} sx={{ mb: 2 }} alignItems="stretch">
              {isSectionVisible(dashboardConfig, 'alerts') && (
                <Grid item xs={12} lg={isSectionVisible(dashboardConfig, 'sales_overview') ? 5 : 12}>
                  <AlertsPanel alerts={alerts} loading={loading} />
                </Grid>
              )}
              {isSectionVisible(dashboardConfig, 'sales_overview') && (
                <Grid item xs={12} lg={isSectionVisible(dashboardConfig, 'alerts') ? 7 : 12}>
                  <SalesOverviewRenderer
                    template={normalizeSalesOverviewTemplate(dashboardConfig?.salesChart)}
                    labels={chartLabels}
                    data={chartData}
                    metrics={salesMetrics}
                    loading={loading || salesChartLoading}
                    period={salesOverviewPeriod}
                    onPeriodChange={setSalesOverviewPeriod}
                    showAiAssistant={aiAssistantEnabled}
                    onAiAssistantClick={() => openAiForInsight(INSIGHT_CONTEXT.SALES_OVERVIEW)}
                  />
                </Grid>
              )}
            </Grid>
          )}

          {(isSectionVisible(dashboardConfig, 'inventory_health') ||
            isSectionVisible(dashboardConfig, 'low_stock') ||
            isSectionVisible(dashboardConfig, 'best_sellers')) && (
            <Grid container spacing={2} sx={{ mb: 2 }} alignItems="stretch">
              {isSectionVisible(dashboardConfig, 'inventory_health') && (
                <Grid item xs={12} md={inventoryRowColSpan} sx={{ display: 'flex' }}>
                  <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                    <InventoryHealthGrid
                      stats={inventoryStats}
                      loading={loading}
                      onViewAll={() => navigate('/reports/stock')}
                      showAiAssistant={aiAssistantEnabled}
                      onAiAssistantClick={() => openAiForInsight(INSIGHT_CONTEXT.INVENTORY_HEALTH)}
                    />
                  </Box>
                </Grid>
              )}
              {isSectionVisible(dashboardConfig, 'low_stock') && (
                <Grid item xs={12} md={inventoryRowColSpan} sx={{ display: 'flex' }}>
                  <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                    <LowStockTable
                      items={lowStockItems}
                      loading={loading}
                      onViewAll={() => navigate('/reports/stock')}
                    />
                  </Box>
                </Grid>
              )}
              {isSectionVisible(dashboardConfig, 'best_sellers') && (
                <Grid item xs={12} md={inventoryRowColSpan} sx={{ display: 'flex' }}>
                  <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                    <BestSellersTable
                      items={topProducts}
                      loading={bestSellersLoading}
                      period={bestSellersPeriod}
                      onPeriodChange={setBestSellersPeriod}
                      showAiAssistant={aiAssistantEnabled}
                      onAiAssistantClick={() => openAiForInsight(INSIGHT_CONTEXT.BEST_SELLERS)}
                    />
                  </Box>
                </Grid>
              )}
            </Grid>
          )}

          {(isSectionVisible(dashboardConfig, 'pending_payments') ||
            isSectionVisible(dashboardConfig, 'recent_activity')) && (
            <Grid container spacing={2} alignItems="stretch">
              {isSectionVisible(dashboardConfig, 'pending_payments') && (
                <Grid item xs={12} md={paymentsRowColSpan} sx={{ display: 'flex' }}>
                  <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                    <PendingPaymentsList
                      customers={creditCustomers}
                      loading={loading}
                      onViewAll={() => navigate('/reports/credit-customers')}
                    />
                  </Box>
                </Grid>
              )}
              {isSectionVisible(dashboardConfig, 'recent_activity') && (
                <Grid item xs={12} md={paymentsRowColSpan} sx={{ display: 'flex' }}>
                  <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                    <RecentActivityFeed
                      items={activityLogs}
                      loading={loading}
                      onViewAll={() => navigate('/settings/activity-log')}
                    />
                  </Box>
                </Grid>
              )}
            </Grid>
          )}
        </Container>
      </Box>

      <Tooltip title="New Bill" placement="left">
        <Fab
          color="primary"
          aria-label="new bill"
          onClick={() => navigate('/sales/pos-keyboard')}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            bgcolor: DASHBOARD_COLORS.accent,
            '&:hover': { bgcolor: DASHBOARD_COLORS.accentHover },
          }}
        >
          <AddIcon />
        </Fab>
      </Tooltip>

      {aiAssistantEnabled && (
        <>
          <AskGeminiFab onClick={openAiChatGeneral} />
          <AiChatDrawer
            open={aiChatOpen}
            onClose={() => setAiChatOpen(false)}
            buildSnapshot={buildAiSnapshot}
            snapshotVersion={aiSnapshotVersion}
            insightContext={aiInsightContext}
            onInsightContextHandled={() => setAiInsightContext(null)}
          />
        </>
      )}
    </Layout>
  );
};

export default DashboardPage;
